import { forwardRef, useMemo, useState } from 'react';
import type { Ref } from 'react';
import type { TextInput as RNTextInput } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue, isIos, useComposedRefs, useProps } from '@tamagui/core';

import { PasswordHide, PasswordShow } from '../../../__generated__/icons';
import { AnalyticsProps } from '../../../helpers/analytics';
import { getLineHeightTokens } from '../../../helpers/fontTokens';
import { isKeyOfObject } from '../../../helpers/utils';
import { Box, BoxProps, PressableBox } from '../../Box';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { Text, TextProps } from '../../Text';
import { useFrameFocus, useInputId, useInputName, useInputState } from './hooks';
import { InputError } from './InputError';
import { InputFrame } from './InputFrame';
import { InputLabel } from './InputLabel';
import type { InputProps, InputStateProp } from './InputProps';
import { TextInputField } from './TextInputField';
import type { TextInputFieldProps } from './TextInputField';

const { textInput: textInputTokens } = tokens;

const PrefixSuffixText = ({
  size,
  state,
  children,
  ...props
}: React.PropsWithChildren<
  Required<Pick<TextInputFieldProps, 'size'>> &
    InputStateProp &
    Omit<TextProps, 'tag' | 'typography' | 'color'>
>) => {
  return (
    <Text
      tag="span"
      typography={textInputTokens.typography.valueText[size]}
      color={
        isKeyOfObject(state, textInputTokens.color.content.prefixSuffix.state)
          ? textInputTokens.color.content.prefixSuffix.state[state]
          : textInputTokens.color.content.prefixSuffix.default
      }
      {...props}
    >
      {children}
    </Text>
  );
};

export type TextInputBaseProps = Partial<AnalyticsProps> &
  InputProps &
  Pick<
    TextInputFieldProps,
    | 'multiline'
    | 'onFocus'
    | 'onBlur'
    | 'onChangeText'
    | 'onChange'
    | 'onKeyPress'
    | 'autoCapitalize'
    | 'autoComplete'
    | 'autoCorrect'
    | 'autoFocus'
    | 'value'
    | 'placeholder'
    | 'keyboardType'
    | 'inputMode'
    | 'maxLength'
    | 'returnKeyType'
    | 'enterKeyHint'
    | 'secureTextEntry'
    | 'clearTextOnFocus'
    | 'enablesReturnKeyAutomatically'
    | 'passwordRules'
    | 'spellCheck'
    | 'textContentType'
    | 'importantForAutofill'
  > & {
    /**
     * An optional string to visually prepend to the input
     */
    prefix?: string;
    /**
     * An optional string to visually append to the input
     */
    suffix?: string;
    /**
     * Suggested text for the input value
     */
    suggestion?: string;
    /**
     * Callback for when the suggestion is clicked
     */
    onSuggestion?: () => void;
    /**
     * Optional props to pass to the frame component (e.g., to tweak padding)
     */
    frameProps?: BoxProps;
    /**
     * Optional children to render inside the frame on the right side (useful
     * for toggles, loading spinners, etc.)
     */
    children?: React.ReactNode;
    /**
     * Optional property to pre-fill the value of the input - value should not update
     */
    prefilledValue?: string;
  };

/**
 * TextInputBase is the base component used for input components like TextInput and TextArea
 */
export const TextInputBase = forwardRef(function TextInputBase(
  {
    title,
    supportText,
    id,
    name,
    value,
    prefilledValue,
    placeholder,
    errorText,
    invalid,
    disabled,
    required,
    prefix,
    suffix,
    multiline,
    onFocus,
    onBlur,
    onChange,
    onChangeText,
    analyticsName,
    trackImpression,
    textContentType,
    suggestion,
    onSuggestion,
    frameProps,
    children,
    ...propsIn
  }: TextInputBaseProps,
  ref: Ref<RNTextInput>,
) {
  const { size = 'medium', ...props } = useProps(propsIn);

  const [showPassword, setShowPassword] = useState(textContentType === 'password');

  const inputId = useInputId(id);

  const elementRef = useInputName(name);
  const combinedRefs = useComposedRefs(ref, elementRef);

  const { inputRef, handleFramePress } = useFrameFocus(combinedRefs);

  const { state, handleFocus, handleBlur } = useInputState({
    disabled,
    errorText,
    invalid,
    onBlur,
    onFocus,
  });

  const heightProps = useMemo(() => getInputHeightProps({ multiline, size }), [multiline, size]);

  const [internalValue, setInternalValue] = useState(value ?? prefilledValue ?? '');
  const handleTextChange = (text: string) => {
    setInternalValue(text);
    onChangeText?.(text);
  };

  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      gap={textInputTokens.padding.gap.textAndField[size]}
      eventName="inputViewEvent"
    >
      <InputLabel
        size={size}
        state={state}
        title={title}
        supportText={supportText}
        id={inputId}
        required={required}
      />
      <InputFrame size={size} state={state} onPress={handleFramePress} {...frameProps}>
        <Box
          flex={1}
          flexDirection="row"
          alignItems="center"
          gap={textInputTokens.padding.gap.prefixValueSuffix.default}
        >
          {!!prefix && typeof prefix === 'string' && (
            <PrefixSuffixText size={size} state={state}>
              {prefix}
            </PrefixSuffixText>
          )}
          <Box flexShrink={1} flexGrow={1}>
            <TextInputField
              secureTextEntry={showPassword}
              size={size}
              color={
                isKeyOfObject(state, textInputTokens.color.content.valueText.state)
                  ? textInputTokens.color.content.valueText.state[state]
                  : textInputTokens.color.content.valueText.default
              }
              id={inputId}
              value={value}
              defaultValue={prefilledValue}
              placeholder={placeholder}
              disabled={disabled}
              editable={!disabled}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={onChange}
              onChangeText={handleTextChange}
              ref={inputRef}
              aria-invalid={!!errorText || invalid}
              aria-required={required}
              multiline={multiline}
              textContentType={textContentType}
              {...heightProps}
              {...props}
            />
          </Box>
          {!!suffix && typeof suffix === 'string' && (
            <>
              {/* invisible suffix aligned to the right because the text field has flexGrow={1} */}
              <Box opacity={0}>
                <PrefixSuffixText size={size} state={state} aria-hidden={true}>
                  {suffix}
                </PrefixSuffixText>
              </Box>
              <Box
                flexDirection="row"
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                alignItems="center"
                gap={textInputTokens.padding.gap.prefixValueSuffix.default}
                aria-hidden={true}
              >
                {/* invisible prefix that floats over the text field and displays to the left of the user input */}
                {!!prefix && typeof prefix === 'string' && (
                  <PrefixSuffixText size={size} state={state} aria-hidden={true}>
                    {prefix}
                  </PrefixSuffixText>
                )}
                {/* invisible text that floats over the text field and is the same width as the user input */}
                <Text
                  tag="span"
                  typography={textInputTokens.typography.valueText[size]}
                  numberOfLines={1}
                  flexShrink={1}
                  opacity={0}
                  aria-hidden={true}
                >
                  {value ?? prefilledValue ?? internalValue}
                </Text>
                {/* visible suffix that floats over the text field and displays immediately to the right of the user input */}
                <PrefixSuffixText size={size} state={state} aria-hidden={false}>
                  {suffix}
                </PrefixSuffixText>
              </Box>
            </>
          )}
        </Box>

        {textContentType === 'password' && (
          <PressableBox
            aria-label={'Toggle password visibility'}
            testID="show-password-toggle"
            analyticsName="show-password-toggle"
            onPress={() => {
              setShowPassword(!showPassword);
            }}
          >
            {showPassword ? <PasswordShow /> : <PasswordHide />}
          </PressableBox>
        )}

        {children}
      </InputFrame>

      <InputError errorText={errorText} />

      {!suggestion ? null : (
        <PressableBox
          aria-label=""
          analyticsName="input-suggestion"
          onPress={onSuggestion}
          testID="input-suggestion"
          justifyContent="flex-start"
        >
          <Text
            tag="span"
            mt="$4x"
            color="$contentAccentBrand"
            cursor="pointer"
            typography={textInputTokens.typography.supportText.default}
          >
            Did you mean "{suggestion}"?
          </Text>
        </PressableBox>
      )}
    </TrackableContainer>
  );
});

const getInputHeightProps = ({
  multiline,
  size,
}: Pick<TextInputBaseProps, 'multiline' | 'size'>) => {
  const props: Pick<
    TextInputFieldProps,
    'numberOfLines' | 'height' | 'lineHeight' | 'textAlignVertical' | 'paddingVertical'
  > = {};

  const lineHeightToken = textInputTokens.typography.valueText[size];
  const lineHeightTokenVariable = getLineHeightTokens()?.[lineHeightToken];
  const lineHeightTokenValue =
    typeof lineHeightTokenVariable === 'number'
      ? lineHeightTokenVariable
      : lineHeightTokenVariable?.val;

  // For some reason providing a lineHeight to single-line inputs on iOS cuts off
  // descenders like "g" and "y", so for now let's only provide it for Android/web
  // and use height on iOS instead. Possibly fixed in a later version of React Native
  // by this PR: https://github.com/facebook/react-native/pull/38359
  if (isIos && !multiline) {
    props.height =
      lineHeightTokenVariable && typeof lineHeightTokenVariable !== 'number'
        ? lineHeightTokenVariable?.val
        : undefined;
  } else {
    props.lineHeight = lineHeightToken;
  }

  if (multiline) {
    const numberOfLines = getTokenValue(tokens.textInput.text.row.multiline.default, 'text');

    props.numberOfLines = numberOfLines;
    props.textAlignVertical = 'top';
    props.lineHeight = lineHeightToken;

    // numberOfLines has issues on iOS and Android, so we need to manually calculate the height
    props.height =
      typeof numberOfLines === 'number' && typeof lineHeightTokenValue === 'number'
        ? numberOfLines * lineHeightTokenValue
        : undefined;
    props.paddingVertical = 0;
  }

  return props;
};
